import { ReactNode } from 'react';
import { useLogger } from '@packages/shared/core/common/hooks';
import { QuizScreenProps, QuizStepData, StepTypes } from '@packages/shared/core/quiz/entities';
import AboutAppQuizStep from './AboutAppQuizStep';
import AnimationInformationQuizStep from './AnimationInformationQuizStep';
import BlurredPasswordStep from './BlurredPasswordStep';
import BlurredSignUpStep from './BlurredSignUpStep';
import ChatQuestionStep from './ChatQuestionStep';
import ChatStep from './ChatStep';
import CheckInIntroQuizStep from './CheckInIntroQuizStep';
import CheckInIntroWithWomanQuizStep from './CheckInIntroWithImageQuizStep';
import ContextAnimationInformationQuizStep from './ContextAnimationInformationQuizStep';
import ContextCheckInIntroQuizStep from './ContextCheckInIntroQuizStep';
import ContextPreferenceIntroStep from './ContextPreferenceIntroStep';
import ContextPreferenceIntroProfileStep from './ContextPreferenceIntroProfileStep';
import ContextReviewsQuizStep from './ContextReviewsQuizStep';
import DateQuizStep from './DateQuizStep';
import DisappearingInformationQuizStep from './DisappearingInformationQuizStep';
import EmailConsentQuizStep from './EmailConsentQuizStep';
import EmailConsentWithRedirectQuizStep from './EmailConsentWithRedirectQuizStep';
import EmailQuizStep from './EmailQuizStep';
import EmailWithDateQuizStep from './EmailWithDateQuizStep';
import ExpertsCatalogueQuizStep from './ExpertsCatalogueQuizStep';
import GenderQuizStep from './GenderQuizStep';
import MentionedInQuizStep from './MentionedInQuizStep';
import MultiAnswerQuizStep from './MultiAnswerQuizStep';
import NameQuizStep from './NameQuizStep';
import NotAloneStep from './NotAloneStep';
import OfferQuizStep from './OfferQuizStep';
import PasswordQuizStep from './PasswordQuizStep';
import PhoneNumberQuizStep from './PhoneNumberQuizStep';
import PlaceQuizStep from './PlaceQuizStep';
import PreferenceIntroStep from './PreferenceIntroStep';
import PreferenceIntroWithImageStep from './PreferenceIntroWithImageStep';
import PrivacySliderStep from './PrivacySliderStep';
import PrivacyStep from './PrivacyStep';
import ProblemQuizStep from './ProblemQuizStep';
import ProcessingQuizStep from './ProcessingQuizStep';
import ProcessingQuizStepWithRedirect from './ProcessingQuizStepWithRedirect';
import ReadingBenefitsStep from './ReadingBenefitsStep';
import RecognizeGoalStep from './RecognizeGoalStep';
import RelateStatementQuizStep from './RelateStatementQuizStep';
import ResonateQuizStep from './ResonateQuizStep';
import ReviewsQuizStep from './ReviewsQuizStep';
import SignInStep from './SignInStep';
import SignUpStep from './SignUpStep';
import SignUpWithGenderStep from './SignUpWithGenderStep';
import SingleAnswerQuizStep from './SingleAnswerQuizStep';
import SingleAnswerWithModalStep from './SingleAnswerWithModalStep';
import SingleHorizontalAnswerQuizStep from './SingleHorizontalAnswerQuizStep';
import SplitStep from './SplitStep';
import SummaryScreenStep from './SummaryScreenStep';
import TestimonialQuizStep from './TestimonialQuizStep';
import TimeQuizStep from './TimeQuizStep';
import TopicToDiscussQuizStep from './TopicToDiscussQuizStep';
import TypeOfReadingQuizStep from './TypeOfReadingQuizStep';
import UserNameQuizStep from './UserNameQuizStep';
import WithCircleChartsStep from './WithCircleChartsStep';
import ThankYouStep from './ThankYouStep';
import SingleAnswerWithFeedbackQuizStep from './SingleAnswerWithFeedbackQuizStep';
import VariousTopicsQuizStep from './VariousTopicsQuizStep';
import CongratulationsStep from './CongratulationsStep';
import AnimatedMessageStep from './AnimatedMessageStep';
import ExpertProcessingQuizStep from './ExpertProcessingQuizStep';
import CategorizedExpertsCatalogueQuizStep from './CategorizedExpertsCatalogueQuizStep';
import ChatExperimentStep from './ChatExperimentStep';
import SingleAnswerV2SingleAnswerQuizStep from './SingleAnswerV2SingleAnswerQuizStep';
import SummaryQuizStep from './SummaryQuizStep';
import ProcessingWithPopupQuizStep from './ProcessingWithPopupQuizStep';
import FreeOfferStep from './FreeOfferStep/FreeOfferStep';
import PhoneNumberWithRedirectQuizStep from './PhoneNumberWithRedirectQuizStep';
import ContextPreferenceIntroProfileStepV2 from './ContextPreferenceIntroProfileStepV2';
import InfoWithImageQuizStep from './InfoWithImageQuizStep';
import RequestBuilderIntroQuizStep from './RequestBuilderIntroQuizStep';
import BuildRequestQuizStep from './BuildRequestQuizStep';
import FinalizeRequestQuizStep from './FinalizeRequestQuizStep';

type QuizStepProps = {
  stepData: QuizStepData;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

export const QuizStep = ({ stepData, renderQuizScreen }: QuizStepProps) => {
  const logger = useLogger();

  switch (stepData.screenType) {
    case StepTypes.ANIMATION_INFORMATION:
      return <AnimationInformationQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.ANIMATED_MESSAGE:
      return <AnimatedMessageStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.DATE:
      return <DateQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PLACE:
      return <PlaceQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.TIME:
      return <TimeQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.DISAPPEARING_INFORMATION:
      return <DisappearingInformationQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CHECK_IN_INTRO:
      return <CheckInIntroQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CHECK_IN_INTRO_WITH_IMAGE:
      return <CheckInIntroWithWomanQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.RESONATE:
      return <ResonateQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PREFERENCE_INTRO:
      return <PreferenceIntroStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PREFERENCE_INTRO_WITH_IMAGE:
      return <PreferenceIntroWithImageStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.RECOGNIZE_GOAL:
      return <RecognizeGoalStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONGRATULATIONS:
      return <CongratulationsStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.GENDER:
      return <GenderQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EMAIL:
      return <EmailQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.REVIEW:
      return <ReviewsQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EMAIL_WITH_DATE:
      return <EmailWithDateQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SINGLE_ANSWER:
      return <SingleAnswerQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SINGLE_ANSWER_V2:
      return (
        <SingleAnswerV2SingleAnswerQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />
      );
    case StepTypes.VARIOUS_TOPICS:
      return <VariousTopicsQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.INFO_WITH_IMAGE:
      return <InfoWithImageQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.REQUEST_BUILDER_INTRO:
      return <RequestBuilderIntroQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.BUILD_REQUEST:
      return <BuildRequestQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.FINALIZE_REQUEST:
      return <FinalizeRequestQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SINGLE_ANSWER_WITH_FEEDBACK:
      return (
        <SingleAnswerWithFeedbackQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />
      );
    case StepTypes.CHAT_QUESTION:
      return <ChatQuestionStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SINGLE_ANSWER_WITH_MODAL:
      return <SingleAnswerWithModalStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PROBLEM:
      return <ProblemQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SINGLE_HORIZONTAL_ANSWER:
      return <SingleHorizontalAnswerQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.MULTI_ANSWER:
      return <MultiAnswerQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.NAME:
      return <NameQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PROCESSING:
      return <ProcessingQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PROCESSING_WITH_POPUP:
      return <ProcessingWithPopupQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EXPERT_PROCESSING:
      return <ExpertProcessingQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PROCESSING_WITH_REDIRECT:
      return <ProcessingQuizStepWithRedirect id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;

    case StepTypes.OFFER:
      return <OfferQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.ABOUT_APP:
      return <AboutAppQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.TESTIMONIAL:
      return <TestimonialQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EMAIL_CONSENT:
      return <EmailConsentQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EMAIL_CONSENT_WITH_REDIRECT:
      return (
        <EmailConsentWithRedirectQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />
      );
    case StepTypes.SIGN_UP:
      return <SignUpStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SIGN_IN:
      return <SignInStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PASSWORD:
      return <PasswordQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PHONE_NUMBER:
      return <PhoneNumberQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PHONE_NUMBER_WITH_REDIRECT:
      return <PhoneNumberWithRedirectQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.TOPIC_TO_DISCUSS:
      return <TopicToDiscussQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.TYPE_OF_READING:
      return <TypeOfReadingQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.EXPERTS_CATALOGUE:
      return <ExpertsCatalogueQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CATEGORIZED_EXPERTS_CATALOGUE:
      return (
        <CategorizedExpertsCatalogueQuizStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />
      );
    case StepTypes.SIGN_UP_WITH_GENDER:
      return <SignUpWithGenderStep id={stepData.id} key={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SPLIT:
      return <SplitStep />;
    case StepTypes.MENTIONED_IN:
      return <MentionedInQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.READING_BENEFITS:
      return <ReadingBenefitsStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.FINAL_SCREEN:
      return <SummaryScreenStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.NOT_ALONE:
      return <NotAloneStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.WITH_CIRCLE_CHARTS:
      return <WithCircleChartsStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CHAT:
      return <ChatStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CHAT_EXPERIMENT:
      return <ChatExperimentStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PRIVACY:
      return <PrivacyStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.PRIVACY_SLIDER:
      return <PrivacySliderStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.BLURRED_SIGNUP:
      return <BlurredSignUpStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.SUMMARY:
      return <SummaryQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.BLURRED_PASSWORD:
      return <BlurredPasswordStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_ANIMATION_INFORMATION:
      return <ContextAnimationInformationQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_REVIEW:
      return <ContextReviewsQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_CHECK_IN_INTRO:
      return <ContextCheckInIntroQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_PREFERENCE_INTRO:
      return <ContextPreferenceIntroStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_PREFERENCE_INTRO_PROFILE:
      return <ContextPreferenceIntroProfileStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.CONTEXT_PREFERENCE_INTRO_PROFILE_V2:
      return <ContextPreferenceIntroProfileStepV2 id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.THANK_YOU:
      return <ThankYouStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.RELATE_STATEMENT:
      return <RelateStatementQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.USER_NAME:
      return <UserNameQuizStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;
    case StepTypes.FREE_OFFER:
      return <FreeOfferStep id={stepData.id} renderQuizScreen={renderQuizScreen} />;

    default: {
      logger.error(new TypeError(`Unknown step data: ${JSON.stringify(stepData)}`));
      return null;
    }
  }
};

export default QuizStep;
